import {Fragment,useState} from 'react';
import {Link} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import type {ProfitQueryInput} from '@hisense/shared';
import {useProductProfit} from '@/api/hooks';
import {Button} from '@/components/ui/Button';
import {LoadingState,ErrorState} from '@/components/ui/States';
import {TableShell,Td,Th,Tr} from '@/components/ui/Table';

/**
 * Lãi theo mã hàng — cùng bộ lọc và cùng công thức với bảng theo chứng từ.
 *
 * Số ở đây gộp lại từ chính báo cáo theo PI, nên hai tab không thể lệch nhau.
 * Hai chỗ phải nói rõ thay vì giấu: mã nào còn dòng chưa biết vốn thì KHÔNG
 * hiện lãi, và tăng/giảm giá ghi ở đầu chứng từ không thuộc mã hàng nào.
 */
export function ProductProfitPanel({params,money}:{params:Partial<ProfitQueryInput>;money:(v:number,currency:string)=>string}){
 const {t}=useTranslation();
 const [invoicedOnly,setInvoicedOnly]=useState(false);
 const [open,setOpen]=useState<string|null>(null);
 const q=useProductProfit({...params,invoicedOnly});
 if(q.isLoading)return <LoadingState/>;
 if(q.error||!q.data)return <ErrorState message={t('profitWorkspace.loadError')} onRetry={()=>void q.refetch()}/>;
 const groups=q.data.groups;
 return <div className="space-y-5">
  <div className="flex flex-wrap items-center gap-2">
   <Button size="sm" variant={invoicedOnly?'primary':'secondary'} aria-pressed={invoicedOnly} onClick={()=>setInvoicedOnly(!invoicedOnly)}>
    {t('profitWorkspace.invoicedOnly')}
   </Button>
   <p className="text-xs text-ink-soft">{t('profitWorkspace.productBasis')}</p>
  </div>
  {groups.every(g=>g.rows.length===0)&&<p className="text-sm text-ink-soft">{t('profitWorkspace.noRows')}</p>}
  {groups.filter(g=>g.rows.length>0).map(group=><section key={group.currency} className="space-y-2">
   <h2 className="font-mono text-sm font-semibold">{group.currency}</h2>
   {group.documentAdjustments!==0&&<p className="text-xs text-warn">
    {t('profitWorkspace.documentAdjustments',{amount:money(group.documentAdjustments,group.currency)})}
   </p>}
   <TableShell>
    <thead><Tr>
     <Th>{t('profitWorkspace.productCode')}</Th><Th>{t('common.description')}</Th>
     <Th align="right">{t('common.quantity')}</Th>
     <Th align="right">{t('profitWorkspace.revenue')}</Th>
     <Th align="right">{t('profitWorkspace.cost')}</Th>
     <Th align="right">{t('profitWorkspace.margin')}</Th>
     <Th align="right">{t('profitWorkspace.marginPct')}</Th>
     <Th align="right">{t('profitWorkspace.piCount')}</Th>
    </Tr></thead>
    <tbody>{group.rows.map(row=><Fragment key={row.code}>
     <Tr>
      <Td><button type="button" className="font-mono text-brand underline" onClick={()=>setOpen(open===row.code?null:row.code)}>{row.code}</button></Td>
      <Td>{row.description??'—'}</Td>
      <Td align="right" className="font-mono tabular">{row.quantity} {row.unit}</Td>
      <Td align="right" className="font-mono tabular">{money(row.revenue,group.currency)}</Td>
      <Td align="right" className="font-mono tabular">{row.cost===null
       ? <span className="text-warn">{t('profitWorkspace.costUnknown',{count:row.incompleteLines})}</span>
       : money(row.cost,group.currency)}</Td>
      <Td align="right" className="font-mono tabular">{row.margin===null?'—':money(row.margin,group.currency)}</Td>
      <Td align="right" className="font-mono tabular">{row.marginPct===null?'—':`${row.marginPct}%`}</Td>
      <Td align="right" className="font-mono tabular">{row.pis.length}</Td>
     </Tr>
     {open===row.code&&<Tr><Td colSpan={8}>
      <ul className="space-y-1 text-xs">{row.pis.map(pi=><li key={pi.piId} className="flex flex-wrap gap-3">
       <Link className="font-mono text-brand underline" to={`/proforma-invoices/${pi.piId}`}>{pi.piNo}</Link>
       <span>{pi.customerName??'—'}</span>
       <span className="font-mono tabular">{pi.quantity}</span>
       <span className="font-mono tabular">{money(pi.revenue,group.currency)}</span>
       <span className="font-mono tabular">{pi.cost===null?'—':money(pi.cost,group.currency)}</span>
      </li>)}</ul>
     </Td></Tr>}
    </Fragment>)}</tbody>
   </TableShell>
  </section>)}
 </div>;
}
